import { ref, type Ref } from 'vue';

interface BulkActionResult {
  processed?: any[];
  errors?: string[];
}

interface BulkActionResponse {
  processed?: any[];
  errors?: string[];
  error?: string;
}

/**
 * Helper function to get CSRF token from meta tag
 */
function getCsrfToken(): string | null {
  const metaTag = document.querySelector('meta[name="csrf-token"]');
  return metaTag ? metaTag.getAttribute('content') : null;
}

/**
 * Composable for managing bulk actions via API
 * @param endpoint - API endpoint URL
 */
export function useBulkActionApi(endpoint: string) {
  const loading: Ref<boolean> = ref(false);

  /**
   * Execute a bulk action on a collection of IDs
   * @param action - The action to execute (string)
   * @param recordIds - Collection of IDs
   * @param parameters - Optional parameters that vary depending on the action
   * @returns Promise with the action result
   */
  async function execute(
    action: string,
    recordIds: (string | number)[],
    parameters?: Record<string, any>
  ): Promise<BulkActionResult> {
    loading.value = true;

    try {
      const csrfToken = getCsrfToken();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      };

      // Add CSRF token if available
      if (csrfToken) {
        headers['X-CSRF-TOKEN'] = csrfToken;
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          action,
          recordIds: recordIds,
          _token: csrfToken,
          ...(parameters || {}),
        }),
      });

      const data: BulkActionResponse = await response.json();

      if (response.ok) {
        return { processed: data.processed || [] };
      } else {
        // Handle validation errors (422) or other errors
        const errors = data.errors || (data.error ? [data.error] : ['An error occurred']);
        return { errors };
      }
    } catch (error) {
      console.error('Error executing bulk action:', error);
      return {
        errors: ['Server connection error']
      };
    } finally {
      loading.value = false;
    }
  }

  return {
    loading,
    execute,
  };
}
